import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    specialty: '',
    institution: '',
    role: 'doctor'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { register } = useAuth();

  const specialties = [
    'Cardiology',
    'Endocrinology',
    'Neurology',
    'Oncology',
    'Psychiatry',
    'Orthopedics',
    'Pediatrics',
    'Geriatrics',
    'Emergency Medicine',
    'Internal Medicine',
    'Other'
  ];

  const roles = [
    { value: 'doctor', label: 'Doctor/Physician' },
    { value: 'researcher', label: 'Medical Researcher' },
    { value: 'hod', label: 'Head of Department' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    const userData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      specialty: formData.specialty,
      institution: formData.institution,
      role: formData.role
    };

    const result = await register(userData);
    
    if (result.success) {
      // Show registration complete message - do NOT navigate to dashboard
      setRegistrationComplete(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Show success message after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Left Panel - Success Message */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16 xl:px-24 bg-white">
          <div className="w-full max-w-md mx-auto">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_exlva-training/artifacts/8u9wfip8_Exlva_logo.png" 
                alt="Exlva Logo" 
                className="h-10 w-auto"
              />
            </div>

            {/* Success Icon */}
            <div className="mx-auto h-20 w-20 flex items-center justify-center bg-green-100 rounded-full mb-6">
              <svg className="h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            {/* Success Title */}
            <h2 className="text-center text-2xl font-bold text-gray-900 mb-4">
              Registration Submitted Successfully!
            </h2>
            
            {/* Success Message */}
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-6 mb-6">
              <p className="text-gray-700 text-center leading-relaxed">
                Thank you for signing up for <strong>Exlva DARe</strong>.
              </p>
              <p className="text-gray-700 text-center leading-relaxed mt-4">
                Your account request has been sent to the administrator for approval.
              </p>
              <p className="text-gray-700 text-center leading-relaxed mt-4">
                Once your account is activated, you will receive an email notification.
              </p>
            </div>
            
            {/* What's Next Section */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                What happens next?
              </h3>
              <ul className="text-sm text-gray-600 space-y-2 ml-7">
                <li>• Our administrator will review your registration request</li>
                <li>• You will receive an email once your account is activated</li>
                <li>• After activation, you can log in and access all features</li>
              </ul>
            </div>
            
            {/* Back to Login Button */}
            <Link 
              to="/login" 
              className="w-full flex items-center justify-center py-3 px-4 text-white font-semibold rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Return to Login Page
            </Link>
          </div>
        </div>

        {/* Right Panel - Hero Image Section */}
        <div className="hidden lg:block lg:w-1/2 relative" style={{ background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-hover) 100%)' }}>
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=1200&fit=crop)',
            }}
          >
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-hover) 100%)', opacity: 0.92 }}></div>
          </div>

          <div className="relative h-full flex flex-col justify-center px-12 xl:px-16">
            <div className="max-w-lg">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>

              <h2 className="text-3xl xl:text-4xl font-bold text-white mb-4 leading-tight">
                Welcome to Exlva DARe
              </h2>

              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                Your registration is being processed. You'll be part of a powerful medical analytics platform.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel - Form Section */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 py-8 sm:px-12 lg:px-16 xl:px-24 bg-white overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_exlva-training/artifacts/8u9wfip8_Exlva_logo.png" 
              alt="Exlva Logo" 
              className="h-10 w-auto"
            />
          </div>

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create an account</h1>
            <p className="text-gray-600">Register as a medical professional</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                autoComplete="name"
                className="w-full px-4 py-3 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                style={{ borderColor: 'var(--theme-border)', '--tw-ring-color': 'var(--theme-primary)' }}
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                className="w-full px-4 py-3 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                style={{ borderColor: 'var(--theme-border)', '--tw-ring-color': 'var(--theme-primary)' }}
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            {/* Role */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1.5">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                name="role"
                required
                className="w-full px-4 py-3 border rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                style={{ borderColor: 'var(--theme-border)', '--tw-ring-color': 'var(--theme-primary)' }}
                value={formData.role}
                onChange={handleChange}
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>

            {/* Two Column Layout for Specialty and Institution */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Specialty */}
              <div>
                <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Specialty
                </label>
                <select
                  id="specialty"
                  name="specialty"
                  className="w-full px-4 py-3 border rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ borderColor: 'var(--theme-border)', '--tw-ring-color': 'var(--theme-primary)' }}
                  value={formData.specialty}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {specialties.map(specialty => (
                    <option key={specialty} value={specialty}>{specialty}</option>
                  ))}
                </select>
              </div>

              {/* Institution */}
              <div>
                <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Institution
                </label>
                <input
                  id="institution"
                  name="institution"
                  type="text"
                  className="w-full px-4 py-3 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ borderColor: 'var(--theme-border)', '--tw-ring-color': 'var(--theme-primary)' }}
                  placeholder="Hospital/Clinic"
                  value={formData.institution}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-16 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ borderColor: 'var(--theme-border)', '--tw-ring-color': 'var(--theme-primary)' }}
                  placeholder="Minimum 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <span className="text-xs font-medium uppercase tracking-wider">
                    {showPassword ? 'HIDE' : 'SHOW'}
                  </span>
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-16 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ borderColor: 'var(--theme-border)', '--tw-ring-color': 'var(--theme-primary)' }}
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  <span className="text-xs font-medium uppercase tracking-wider">
                    {showConfirmPassword ? 'HIDE' : 'SHOW'}
                  </span>
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 text-white font-semibold rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--theme-primary)' }}
              onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = 'var(--theme-primary-hover)')}
              onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = 'var(--theme-primary)')}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold" style={{ color: 'var(--theme-primary)' }}>
                Sign in
              </Link>
            </p>
          </div>

          {/* Security Notice */}
          <div className="mt-6 p-4 rounded-lg border" style={{ backgroundColor: 'var(--theme-primary-light)', borderColor: 'var(--theme-border)' }}>
            <div className="flex items-start">
              <svg className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-gray-500 leading-relaxed">
                <span className="font-semibold text-gray-700">Security & Compliance:</span> All data is encrypted and compliant with HIPAA and GDPR standards.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Hero Image Section */}
      <div className="hidden lg:block lg:w-1/2 relative" style={{ background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-hover) 100%)' }}>
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=1200&fit=crop)',
          }}
        >
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-hover) 100%)', opacity: 0.92 }}></div>
        </div>

        {/* Content Overlay */}
        <div className="relative h-full flex flex-col justify-center px-12 xl:px-16">
          <div className="max-w-lg">
            {/* Icon */}
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl xl:text-4xl font-bold text-white mb-4 leading-tight">
              Join Exlva DARe Today
            </h2>

            {/* Description */}
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Create your account and get access to powerful AI-driven medical data analytics tools.
            </p>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-center text-white/90">
                <svg className="w-5 h-5 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Quick approval process</span>
              </div>
              <div className="flex items-center text-white/90">
                <svg className="w-5 h-5 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Secure medical data handling</span>
              </div>
              <div className="flex items-center text-white/90">
                <svg className="w-5 h-5 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Team collaboration features</span>
              </div>
              <div className="flex items-center text-white/90">
                <svg className="w-5 h-5 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>AI-powered data extraction</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;